/* eslint-disable react-hooks/exhaustive-deps */
import type { DropAnimation, UniqueIdentifier } from '@dnd-kit/core';
import {
  closestCenter,
  defaultDropAnimationSideEffects,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MeasuringStrategy,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { AnimateLayoutChanges, NewIndexGetter } from '@dnd-kit/sortable';
import {
  arrayMove,
  arraySwap,
  defaultAnimateLayoutChanges,
  rectSortingStrategy,
  rectSwappingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';

import { useEffect, useRef, useState } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Portal from '@mui/material/Portal';

import { TransitionGroup } from 'react-transition-group';
import Collapse from '@mui/material/Collapse';
import { itemClasses } from './classes';
import ItemBase from './sortable-item-base';

// ----------------------------------------------------------------------

const dropAnimationConfig: DropAnimation = {
  sideEffects: defaultDropAnimationSideEffects({ styles: { active: { opacity: '0.5' } } }),
};

type Props = {
  swap?: boolean;
  idList: string[];
  layout?: 'grid' | 'vertical' | 'horizontal';
  renderItem?: (item: UniqueIdentifier) => React.ReactNode;
  isSelected?: (id: UniqueIdentifier) => boolean;
  sendNewOrder: (newOrder: UniqueIdentifier[]) => void;
  loading?: boolean;
  type?: string;
};

export function SortableContainer({
  idList,
  swap = false,
  layout = 'grid',
  renderItem,
  isSelected,
  sendNewOrder,
  loading,
  type = 'list',
}: Props) {
  const [items, setItems] = useState<UniqueIdentifier[]>(idList);

  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const isFirstAnnouncement = useRef(true);

  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);

  const getIndex = (id: UniqueIdentifier) => items.indexOf(id);

  const activeIndex = activeId ? getIndex(activeId) : -1;

  const strategy = swap ? rectSwappingStrategy : rectSortingStrategy;
  const reorderItems = swap ? arraySwap : arrayMove;

  const getNewIndex = swap
    ? ({
        id,
        items: currentItems,
        activeIndex: currentIndex,
        overIndex,
      }: NewIndexGetter['arguments']) =>
        reorderItems(currentItems, currentIndex, overIndex).indexOf(id)
    : undefined;

  useEffect(() => {
    if (!activeId) {
      isFirstAnnouncement.current = true;
    }
  }, [activeId]);

  useEffect(() => {
    if (!loading) setItems(idList);
  }, [loading, idList]);

  const handleRemove = (id: UniqueIdentifier) => {
    const updatedItems = items.filter((item) => item !== id);
    setItems(updatedItems);
  };

  return (
    <Stack alignItems="center">
      <DndContext
        id="dnd-grid"
        sensors={sensors}
        collisionDetection={closestCenter}
        measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
        onDragStart={({ active }) => {
          if (!active) {
            return;
          }

          setActiveId(active.id);
        }}
        onDragEnd={({ over }) => {
          setActiveId(null);

          if (over) {
            const overIndex = getIndex(over.id);
            if (activeIndex !== overIndex) {
              const newOrder = reorderItems(items, activeIndex, overIndex);
              setItems(newOrder);
              sendNewOrder(newOrder);
            }
          }
        }}
      >
        <SortableContext items={items} strategy={strategy}>
          <Box
            component="ul"
            sx={{
              p: 0,
              width: 1,
              ...(layout === 'grid' && {
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
              }),
              ...(layout === 'vertical' && { display: 'flex', flexDirection: 'column' }),
              ...(layout === 'horizontal' && {
                display: 'flex',
                overflowX: 'auto',
                flexDirection: 'row',
                [`& .${itemClasses.itemWrap}`]: { maxWidth: 180 },
              }),
            }}
          >
            <TransitionGroup>
              {items.map((item, index) => (
                <Collapse key={item}>
                  <SortableGridItem
                    id={item}
                    index={index}
                    getNewIndex={getNewIndex}
                    onRemove={() => handleRemove(item)}
                    renderItem={renderItem}
                    isSelected={isSelected}
                    type={type}
                  />
                </Collapse>
              ))}
            </TransitionGroup>
          </Box>
        </SortableContext>

        <Portal>
          <DragOverlay dropAnimation={dropAnimationConfig}>
            {activeId ? (
              <ItemBase
                item={items[activeIndex]}
                stateProps={{ dragOverlay: true }}
                renderItem={renderItem}
                type={type}
              />
            ) : null}
          </DragOverlay>
        </Portal>
      </DndContext>
    </Stack>
  );
}

// ----------------------------------------------------------------------

const animateLayoutChanges: AnimateLayoutChanges = (args) =>
  defaultAnimateLayoutChanges({ ...args, wasDragging: true });

interface SortableItemProps {
  index: number;
  id: UniqueIdentifier;
  getNewIndex?: NewIndexGetter;
  onRemove?: () => void;
  renderItem?: (item: UniqueIdentifier) => React.ReactNode;
  isSelected?: (id: UniqueIdentifier) => boolean;
  type?: string;
}

export function SortableGridItem({
  id,
  index,
  onRemove,
  getNewIndex,
  renderItem,
  isSelected,
  type = 'list',
}: SortableItemProps) {
  const {
    isSorting,
    transform,
    listeners,
    attributes,
    isDragging,
    setNodeRef,
    transition,
    setActivatorNodeRef,
  } = useSortable({ id, getNewIndex, animateLayoutChanges });

  return (
    <ItemBase
      ref={setNodeRef}
      item={id}
      data-id={id}
      data-index={index}
      onRemove={onRemove}
      stateProps={{
        listeners,
        transform,
        transition,
        sorting: isSorting,
        dragging: isDragging,
        dragOverlay: isDragging,
        handleProps: { ref: setActivatorNodeRef },
      }}
      {...attributes}
      renderItem={renderItem}
      isSelected={isSelected}
      type={type}
      isDragging={isDragging} // Pass the dragging state
    />
  );
}
